import {
  ClearValueButton,
  PasteFromClipboardButton,
} from '@drodil/backstage-plugin-toolbox';
import {
  Button,
  TextField,
  Typography,
  useTheme,
  Paper,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import ScheduleIcon from '@material-ui/icons/Schedule';

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  validIcon: {
    color: theme.palette.success.main,
    marginRight: theme.spacing(1),
  },
  errorIcon: {
    color: theme.palette.error.main,
    marginRight: theme.spacing(1),
  },
  exampleChip: {
    margin: theme.spacing(0.5),
    cursor: 'pointer',
  },
  helpSection: {
    marginTop: theme.spacing(2),
  },
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: 200,
  },
}));

interface CronPreset {
  label: string;
  expression: string;
  description: string;
}

const cronPresets: CronPreset[] = [
  {
    label: 'Every minute',
    expression: '* * * * *',
    description: 'Runs every minute',
  },
  {
    label: 'Every 5 minutes',
    expression: '*/5 * * * *',
    description: 'Runs every 5 minutes',
  },
  {
    label: 'Every 15 minutes',
    expression: '*/15 * * * *',
    description: 'Runs every 15 minutes',
  },
  {
    label: 'Every 30 minutes',
    expression: '*/30 * * * *',
    description: 'Runs every 30 minutes',
  },
  {
    label: 'Every hour',
    expression: '0 * * * *',
    description: 'Runs at minute 0 of every hour',
  },
  {
    label: 'Every day at midnight',
    expression: '0 0 * * *',
    description: 'Runs at 00:00 every day',
  },
  {
    label: 'Every day at noon',
    expression: '0 12 * * *',
    description: 'Runs at 12:00 every day',
  },
  {
    label: 'Every weekday at 9 AM',
    expression: '0 9 * * 1-5',
    description: 'Runs at 09:00 Monday through Friday',
  },
  {
    label: 'Every Monday at 9 AM',
    expression: '0 9 * * 1',
    description: 'Runs at 09:00 every Monday',
  },
  {
    label: 'First day of month',
    expression: '0 0 1 * *',
    description: 'Runs at 00:00 on the 1st of every month',
  },
  {
    label: 'Every Sunday at 2 AM',
    expression: '0 2 * * 0',
    description: 'Runs at 02:00 every Sunday',
  },
];

export function CronHelper() {
  const classes = useStyles();
  const theme = useTheme();
  const [cronExpression, setCronExpression] = useState('');
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Simple builder state
  const [minute, setMinute] = useState('*');
  const [hour, setHour] = useState('*');
  const [dayOfMonth, setDayOfMonth] = useState('*');
  const [month, setMonth] = useState('*');
  const [dayOfWeek, setDayOfWeek] = useState('*');

  const validateCronExpression = (expression: string): boolean => {
    if (!expression.trim()) {
      setErrorMessage('Cron expression cannot be empty');
      return false;
    }

    const parts = expression.trim().split(/\s+/);

    if (parts.length !== 5 && parts.length !== 6) {
      setErrorMessage(
        `Invalid cron expression: Expected 5 or 6 fields, got ${parts.length}`,
      );
      return false;
    }

    // Basic validation for each field - allow wildcards, numbers, ranges, steps, and lists
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      // Allow wildcards (*), no specific value (?), numbers, ranges (-), steps (/), and lists (,)
      if (!/^[0-9,\-/*?]+$/.test(part)) {
        setErrorMessage(`Invalid character in field ${i + 1}: "${part}"`);
        return false;
      }
    }

    setErrorMessage('');
    return true;
  };

  const handleValidate = () => {
    const valid = validateCronExpression(cronExpression);
    setIsValid(valid);
  };

  const handleExpressionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCronExpression(event.target.value);
    setIsValid(null);
  };

  const handlePresetClick = (preset: CronPreset) => {
    setCronExpression(preset.expression);
    setIsValid(null);
  };

  const handleBuildExpression = () => {
    const built = `${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`;
    setCronExpression(built);
    setIsValid(null);
  };

  const handleClear = () => {
    setCronExpression('');
    setIsValid(null);
    setErrorMessage('');
  };

  const handlePasteFromClipboard = (text: string) => {
    setCronExpression(text);
    setIsValid(null);
    setErrorMessage('');
  };

  return (
    <Grid container spacing={3}>
      {/* Main Input Section */}
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            <ScheduleIcon
              aria-hidden="true"
              style={{ verticalAlign: 'middle', marginRight: 8 }}
            />
            Cron Expression Validator
          </Typography>

          <TextField
            fullWidth
            label="Cron Expression"
            placeholder="* * * * * (minute hour day month weekday)"
            value={cronExpression}
            onChange={handleExpressionChange}
            variant="outlined"
            margin="normal"
            helperText="Enter a cron expression in standard 5-field format"
            inputProps={{
              'aria-describedby': 'cron-helper-text',
            }}
            InputProps={{
              endAdornment: (
                <>
                  <PasteFromClipboardButton
                    setInput={handlePasteFromClipboard}
                  />
                  <ClearValueButton setValue={handleClear} />
                </>
              ),
            }}
          />
          <span id="cron-helper-text" style={{ display: 'none' }}>
            Enter a cron expression in standard 5-field format
          </span>

          <Grid container spacing={2} style={{ marginTop: 16 }}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={handleValidate}
                disabled={!cronExpression.trim()}
                aria-describedby="cron-helper-text"
              >
                Validate & Preview
              </Button>
            </Grid>
          </Grid>

          {isValid !== null && (
            <Paper
              style={{
                marginTop: 16,
                padding: 16,
                backgroundColor: isValid
                  ? theme.palette.success.light
                  : theme.palette.error.light,
              }}
            >
              {isValid ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon
                    aria-hidden="true"
                    className={classes.validIcon}
                  />
                  <Typography variant="body1">
                    Valid cron expression!
                  </Typography>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <ErrorIcon aria-hidden="true" className={classes.errorIcon} />
                  <Typography variant="body1">{errorMessage}</Typography>
                </div>
              )}
            </Paper>
          )}
        </Paper>
      </Grid>

      {/* Expression Builder */}
      <Grid item xs={12} md={6}>
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            Expression Builder
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Build your cron expression field by field
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Minute (0-59)"
                value={minute}
                onChange={e => setMinute(e.target.value)}
                variant="outlined"
                helperText="Examples: * (every), 0 (at :00), */5 (every 5), 0,15,30,45 (specific)"
                className={classes.formControl}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Hour (0-23)"
                value={hour}
                onChange={e => setHour(e.target.value)}
                variant="outlined"
                helperText="Examples: * (every), 0 (midnight), 9 (9 AM), 18 (6 PM), 9-17 (range)"
                className={classes.formControl}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Day of Month (1-31)"
                value={dayOfMonth}
                onChange={e => setDayOfMonth(e.target.value)}
                variant="outlined"
                helperText="Examples: * (every day), 1 (1st), 15 (15th), */2 (every 2 days)"
                className={classes.formControl}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Month (1-12)"
                value={month}
                onChange={e => setMonth(e.target.value)}
                variant="outlined"
                helperText="Examples: * (every month), 1 (Jan), 12 (Dec), 1,7 (Jan & Jul)"
                className={classes.formControl}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Day of Week (0-6)"
                value={dayOfWeek}
                onChange={e => setDayOfWeek(e.target.value)}
                variant="outlined"
                helperText="Examples: * (every day), 1-5 (Mon-Fri), 0 (Sun), 6 (Sat)"
                className={classes.formControl}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleBuildExpression}
                fullWidth
              >
                Build Expression
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Common Presets */}
      <Grid item xs={12} md={6}>
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            Common Presets
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Click a preset to use it
          </Typography>

          <div>
            {cronPresets.map((preset, index) => (
              <Chip
                key={index}
                label={`${preset.label}: ${preset.expression}`}
                onClick={() => handlePresetClick(preset)}
                className={classes.exampleChip}
                color="primary"
                variant="outlined"
              />
            ))}
          </div>
        </Paper>
      </Grid>

      {/* Cron Syntax Help */}
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            Cron Expression Syntax Reference
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Field</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Allowed Values</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Special Characters</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Minute</TableCell>
                  <TableCell>0-59</TableCell>
                  <TableCell>* , - /</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Hour</TableCell>
                  <TableCell>0-23</TableCell>
                  <TableCell>* , - /</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Day of Month</TableCell>
                  <TableCell>1-31</TableCell>
                  <TableCell>* , - / ?</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell>1-12 or JAN-DEC</TableCell>
                  <TableCell>* , - /</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Day of Week</TableCell>
                  <TableCell>0-6 or SUN-SAT</TableCell>
                  <TableCell>* , - / ?</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <div className={classes.helpSection}>
            <Typography variant="subtitle2" gutterBottom>
              Special Characters:
            </Typography>
            <Typography variant="body2" component="div">
              <ul>
                <li>
                  <strong>*</strong> - All values (every minute, every hour,
                  etc.)
                </li>
                <li>
                  <strong>,</strong> - List separator (e.g., "1,3,5" = 1st, 3rd,
                  and 5th)
                </li>
                <li>
                  <strong>-</strong> - Range (e.g., "1-5" = 1 through 5)
                </li>
                <li>
                  <strong>/</strong> - Step values (e.g., "*/5" = every 5 units)
                </li>
                <li>
                  <strong>?</strong> - No specific value (only for day fields)
                </li>
              </ul>
            </Typography>
          </div>
        </Paper>
      </Grid>
    </Grid>
  );
}
